import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import { UserAutocompleteResult } from './autocomplete/useUsersAutocomplete';
import type { BaseQuery } from './baseQuery.types';
type AllocationGroup = components['schemas']['GetAllocationGroupsEndpoint_AllocationGroupResponse'];

type AllocationGroupPagedResponse = components['schemas']['GetAllocationGroupsEndpoint_AllocationGroupPagedResponse'];

export interface UseAllocationsGroupReturn extends BaseQuery {
  allocationsGroup: AllocationGroupPagedResponse | null;
}

export function useAllocationGroups(
  page: number = 1,
  pageSize: number = 50,
  sortedBy: string | null = 'name',
  groupNames: AllocationGroup[] | null = null,
  emails: UserAutocompleteResult[] | null = null,
): UseAllocationsGroupReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();

  const getAllocationsGroup = async (
    organizationId: number,
    page: number,
    pageSize: number,
    sortedBy: string | null,
    groupNames: string[] | null,
    emails: string[] | null,
  ) => {
    const client = createApiClient(getAccessToken);
    const { data } = await client.POST('/api/organizations/{organizationId}/allocation-groups', {
      params: {
        path: { organizationId },
        query: {
          Page: page,
          PageSize: pageSize,
          ...(sortedBy ? { SortedBy: sortedBy } : {}),
        },
      },
      body: {
        groupNames: groupNames && groupNames.length > 0 ? groupNames : null,
        usersEmailFilter: emails && emails.length > 0 ? emails : null,
      },
    });

    if (!data) {
      throw new Error('No data returned from API');
    }

    return data as AllocationGroupPagedResponse;
  };

  const extractedGroupNames: string[] | null =
    groupNames && groupNames.length > 0 ? groupNames.map((g) => g.name).filter((name): name is string => typeof name === 'string') : null;

  const extractedEmails: string[] | null =
    emails && emails.length > 0 ? emails.map((u) => u.email).filter((email): email is string => typeof email === 'string') : null;

  const useAllocations = useQuery({
    queryKey: ['allocationsGroup', organizationId, page, pageSize, sortedBy, groupNames, emails],
    queryFn: () => getAllocationsGroup(organizationId, page, pageSize, sortedBy, extractedGroupNames, extractedEmails),
    retry: false,
  });

  const { data, isLoading, isPending, error, refetch, isRefetching } = useAllocations;

  return {
    allocationsGroup: data ?? null,
    isLoading: isLoading || isPending,
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
